import { ApiError } from '@kent/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { generateDeviceFingerprint } from '@/lib/fingerprint';
import { toE164 } from '@/lib/phone';
import { authService } from '@/services/authService';

import type { Applicant, OtpType } from '@/types/auth.types';

interface AuthState {
  // State
  isAuthenticated: boolean;
  contact: string | null;
  contactType: OtpType | null;
  otpSent: boolean;
  otpLastSentAt: number | null;
  otpNextAllowedAt: number | null;
  accessToken: string | null;
  refreshToken: string | null;
  deviceTrustToken: string | null;
  applicant: Applicant | null;
  isLoading: boolean;
  error: string | null;
  lastErrorCode: string | null;
  hasHydrated: boolean;

  // Actions
  setContact: (contact: string, type: OtpType) => void;
  getOtpRetryAfterSeconds: () => number;
  sendOtp: (options?: {
    smsConsent?: boolean;
  }) => Promise<{ ok: true } | { ok: false; error: string; code?: string; retryAfterSeconds?: number }>;
  verifyOtp: (
    code: string,
    rememberDevice: boolean,
  ) => Promise<{ ok: true } | { ok: false; error: string; code?: string; retryAfterSeconds?: number }>;
  loginWithDeviceTrust: () => Promise<{ ok: true } | { ok: false; error: string }>;
  refreshSession: () => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  resetOtpFlow: () => void;
  startGuestSession: () => void;
  setHasHydrated: (hydrated: boolean) => void;
}

const OTP_COOLDOWN_MS = 60 * 1000;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      contact: null,
      contactType: null,
      otpSent: false,
      otpLastSentAt: null,
      otpNextAllowedAt: null,
      accessToken: null,
      refreshToken: null,
      deviceTrustToken: null,
      applicant: null,
      isLoading: false,
      error: null,
      lastErrorCode: null,
      hasHydrated: false,

      setContact: (contact, type) => set({ contact, contactType: type }),

      getOtpRetryAfterSeconds: () => {
        const nextAllowedAt = get().otpNextAllowedAt;
        if (!nextAllowedAt) return 0;
        const diffMs = nextAllowedAt - Date.now();
        return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
      },

      sendOtp: async (options) => {
        const { contact, contactType, getOtpRetryAfterSeconds } = get();

        const retryAfterSeconds = getOtpRetryAfterSeconds();
        if (retryAfterSeconds > 0) {
          return { ok: false, error: 'Rate limited', retryAfterSeconds };
        }

        if (!contact || !contactType) {
          return { ok: false, error: 'Contact information required' };
        }

        set({ isLoading: true, error: null, lastErrorCode: null });

        try {
          const normalizedPhone = contactType === 'sms' ? toE164(contact) : undefined;
          const payload =
            contactType === 'email'
              ? { type: contactType, email: contact }
              : { type: contactType, phone: normalizedPhone ?? contact, smsConsent: options?.smsConsent };

          await authService.requestOtp(payload);

          const now = Date.now();
          set({
            otpSent: true,
            otpLastSentAt: now,
            otpNextAllowedAt: now + OTP_COOLDOWN_MS,
            isLoading: false,
          });

          return { ok: true };
        } catch (err) {
          const code = err instanceof ApiError ? err.errorCode : undefined;
          const retryAfter = err instanceof ApiError ? err.retryAfterSeconds : undefined;
          const message = err instanceof Error ? err.message : 'Failed to send OTP';
          set({ isLoading: false, error: message, lastErrorCode: code ?? null });
          return { ok: false, error: message, code, retryAfterSeconds: retryAfter };
        }
      },

      verifyOtp: async (code, rememberDevice) => {
        const { contact, contactType } = get();

        if (!contact || !contactType) {
          return { ok: false, error: 'Contact information required' };
        }

        set({ isLoading: true, error: null });

        try {
          const normalizedPhone = contactType === 'sms' ? toE164(contact) : undefined;
          const payload =
            contactType === 'email'
              ? { type: contactType, email: contact, code }
              : { type: contactType, phone: normalizedPhone ?? contact, code };

          const response = await authService.verifyOtp(payload);

          set({
            isAuthenticated: true,
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            applicant: response.data.applicant,
            otpSent: false,
            isLoading: false,
          });

          // If user wants to remember device, create device trust
          if (rememberDevice) {
            try {
              const fingerprint = await generateDeviceFingerprint();
              const trustResponse = await authService.createDeviceTrust({ deviceFingerprint: fingerprint });
              set({ deviceTrustToken: trustResponse.data.deviceTrustToken });
            } catch {
              // Non-critical - device trust creation failed but login succeeded
            }
          }

          return { ok: true };
        } catch (err) {
          const errorCode = err instanceof ApiError ? err.errorCode : undefined;
          const retryAfterSeconds = err instanceof ApiError ? err.retryAfterSeconds : undefined;
          const message = err instanceof Error ? err.message : 'Invalid OTP code';
          set({ isLoading: false, error: message, lastErrorCode: errorCode ?? null });
          return { ok: false, error: message, code: errorCode, retryAfterSeconds };
        }
      },

      loginWithDeviceTrust: async () => {
        const { deviceTrustToken } = get();

        if (!deviceTrustToken) {
          return { ok: false, error: 'No device trust token' };
        }

        set({ isLoading: true, error: null });

        try {
          const response = await authService.verifyDeviceTrust({ deviceTrustToken });

          set({
            isAuthenticated: true,
            accessToken: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            applicant: response.data.applicant,
            isLoading: false,
          });

          return { ok: true };
        } catch {
          // Device trust expired or invalid - clear it
          set({
            deviceTrustToken: null,
            isLoading: false,
            error: null, // Don't show error for expired trust
          });
          return { ok: false, error: 'Device trust expired' };
        }
      },

      refreshSession: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          return false;
        }

        try {
          const response = await authService.refreshToken(refreshToken);
          set({ accessToken: response.data.accessToken });
          return true;
        } catch {
          get().logout();
          return false;
        }
      },

      logout: () => {
        set({
          isAuthenticated: false,
          contact: null,
          contactType: null,
          otpSent: false,
          accessToken: null,
          refreshToken: null,
          deviceTrustToken: null,
          applicant: null,
          error: null,
        });
      },

      clearError: () => set({ error: null, lastErrorCode: null }),

      resetOtpFlow: () =>
        set({
          contact: null,
          contactType: null,
          otpSent: false,
          otpLastSentAt: null,
          otpNextAllowedAt: null,
          error: null,
          lastErrorCode: null,
        }),

      startGuestSession: () => {
        set({
          isAuthenticated: false,
          contact: null,
          contactType: null,
        });
      },

      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: 'kent-auth-session',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        deviceTrustToken: state.deviceTrustToken,
        applicant: state.applicant,
        otpLastSentAt: state.otpLastSentAt,
        otpNextAllowedAt: state.otpNextAllowedAt,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);
